import axios, { AxiosResponse, AxiosRequestConfig } from 'axios';
import config from "../config";

const { api } = config;

// default
axios.defaults.baseURL = api.API_URL;
// content type
axios.defaults.headers.post["Content-Type"] = "application/json";
// timeout (30 seconds)
axios.defaults.timeout = 30000;

// Add request interceptor to catch NaN in URLs
axios.interceptors.request.use((config) => {
  if (config.url && config.url.includes('NaN')) {
    console.log('Blocked API request with NaN in URL:', config.url);
    return Promise.reject(new Error('Invalid API request: URL contains NaN'));
  }
  return config;
}, (error) => {
  return Promise.reject(error);
});

// Add response interceptor to handle 401 errors
let isRedirecting = false;
axios.interceptors.response.use(
  (response) => response,
  (error) => {
    // Check if it's a 401 error and we're not already redirecting
    if (error.response?.status === 401 && !isRedirecting) {
      // Don't redirect for login/register endpoints, public routes, and Frame.io endpoints
      const isAuthEndpoint = error.config?.url?.includes('/login') ||
                             error.config?.url?.includes('/register') ||
                             error.config?.url?.includes('/forgot-password') ||
                             error.config?.url?.includes('/public/keys') ||
                             error.config?.url?.includes('/public/door') ||
                             error.config?.url?.includes('/frameio') ||
                             error.config?.url?.includes('frame.io');

      if (!isAuthEndpoint) {
        isRedirecting = true;
        // Clear session
        localStorage.removeItem('authUser');
        sessionStorage.removeItem('authUser');
        // Stop all ongoing requests
        window.location.href = '/login';
      }
    }
    return Promise.reject(error);
  }
);

// Set authorization token from localStorage (or fallback to sessionStorage for migration)
const authUser: any = localStorage.getItem("authUser") || sessionStorage.getItem("authUser");
if (authUser) {
  try {
    const parsed = JSON.parse(authUser);
    const token = parsed.token || parsed.data?.token;
    if (token) {
      axios.defaults.headers.common["Authorization"] = "Bearer " + token;
    }
  } catch (error) {
    console.error('Error parsing authUser from sessionStorage:', error);
  }
}

/**
 * Sets the default authorization
 * @param {*} token
 */
const setAuthorization = (token : string) => {
  axios.defaults.headers.common["Authorization"] = "Bearer " + token;
};

class APIClient {
  /**
   * Get current auth token from localStorage (fallback to sessionStorage)
   */
  private getAuthToken = (): string | null => {
    const authUser = localStorage.getItem("authUser") || sessionStorage.getItem("authUser");
    if (authUser) {
      try {
        const parsed = JSON.parse(authUser);
        // Token kann direkt im Objekt oder im data-Objekt sein
        return parsed.token || parsed.data?.token || null;
      } catch {
        return null;
      }
    }
    return null;
  };

  /**
   * Update axios headers with current token
   */
  private updateAuthHeader = () => {
    const token = this.getAuthToken();
    if (token) {
      axios.defaults.headers.common["Authorization"] = "Bearer " + token;
    } else {
      delete axios.defaults.headers.common["Authorization"];
    }
  };

  /**
   * Fetches data from the given URL
   */
  get = (url: string, params?: any): Promise<AxiosResponse> => {
    this.updateAuthHeader(); // Update token before request
    let response: Promise<AxiosResponse>;

    let paramKeys: string[] = [];

    if (params) {
      Object.keys(params).map(key => {
        paramKeys.push(key + '=' + params[key]);
        return paramKeys;
      });

      const queryString = paramKeys && paramKeys.length ? paramKeys.join('&') : "";
      response = axios.get(`${url}?${queryString}`, params);
    } else {
      response = axios.get(`${url}`, params);
    }

    return response;
  };

  /**
   * Posts the given data to the URL
   */
  create = (url: string, data: any): Promise<AxiosResponse> => {
    this.updateAuthHeader(); // Update token before request

    // For FormData, DO NOT set Content-Type - let axios auto-detect and set it with correct boundary
    // Setting 'multipart/form-data' without boundary breaks the upload!
    const config: AxiosRequestConfig = {};
    if (data instanceof FormData) {
      // Remove default Content-Type to let axios set it automatically with boundary
      config.headers = {};
      delete axios.defaults.headers.post["Content-Type"];
    }

    return axios.post(url, data, config);
  };

  /**
   * Updates data
   */
  update = (url: string, data: any): Promise<AxiosResponse> => {
    this.updateAuthHeader(); // Update token before request

    // For FormData, DO NOT set Content-Type - let axios auto-detect and set it with correct boundary
    const config: AxiosRequestConfig = {};
    if (data instanceof FormData) {
      // Remove default Content-Type to let axios set it automatically with boundary
      config.headers = {};
    }

    return axios.patch(url, data, config);
  };

  put = (url: string, data: any): Promise<AxiosResponse> => {
    this.updateAuthHeader(); // Update token before request

    // For FormData, DO NOT set Content-Type - let axios auto-detect and set it with correct boundary
    const config: AxiosRequestConfig = {};
    if (data instanceof FormData) {
      // Remove Content-Type header to let axios set it automatically with boundary
      config.headers = {};
    }

    return axios.put(url, data, config);
  };

  /**
   * Patch data (alias for update)
   */
  patch = (url: string, data: any): Promise<AxiosResponse> => {
    return this.update(url, data);
  };

  /**
   * Deletes data
   */
  delete = (url: string, config?: AxiosRequestConfig): Promise<AxiosResponse> => {
    this.updateAuthHeader(); // Update token before request
    return axios.delete(url, { ...config });
  };
}

const getLoggedinUser = () => {
  const user = sessionStorage.getItem("authUser");
  if (!user) {
    return null;
  } else {
    return JSON.parse(user);
  }
};

// News helper methods
export const getNewsList = (params?: any) => {
  const client = new APIClient();
  return client.get('/news', params).then(res => res.data);
};

export const getNewsById = (id: number) => {
  const client = new APIClient();
  return client.get(`/news/${id}`).then(res => res.data);
};

export const markNewsAsRead = (id: number) => {
  const client = new APIClient();
  return client.create(`/news/${id}/read`, {}).then(res => res.data);
};

export const getUnreadNewsCount = () => {
  const client = new APIClient();
  return client.get('/news/unread-count').then(res => res.data);
};

export const createNews = (data: any) => {
  const client = new APIClient();
  return client.create('/news', data).then(res => res.data);
};

export const updateNews = (id: number, data: any) => {
  const client = new APIClient();
  return client.update(`/news/${id}`, data).then(res => res.data);
};

export const deleteNews = (id: number) => {
  const client = new APIClient();
  return client.delete(`/news/${id}`).then(res => res.data);
};

export { APIClient, setAuthorization, getLoggedinUser };